import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface LowStockItem {
  id: string;
  product: string;
  sku: string;
  currentStock: number;
  reorderPoint: number;
  suggestedOrder: number;
  location: string;
  supplier: string;
  lastRestocked: string;
  daysUntilStockout: number;
}

const MOCK_LOW_STOCK: LowStockItem[] = [
  { id: "1", product: "USB-C Cable", sku: "ELEC-003", currentStock: 8, reorderPoint: 50, suggestedOrder: 100, location: "Warehouse B", supplier: "TechSupply Co.", lastRestocked: "2024-01-10", daysUntilStockout: 3 },
  { id: "2", product: "Running Shoes", sku: "CLTH-003", currentStock: 18, reorderPoint: 20, suggestedOrder: 40, location: "Warehouse A", supplier: "SportWear Inc.", lastRestocked: "2024-01-12", daysUntilStockout: 7 },
  { id: "3", product: "Laptop Stand", sku: "ELEC-008", currentStock: 5, reorderPoint: 15, suggestedOrder: 30, location: "Warehouse A", supplier: "TechSupply Co.", lastRestocked: "2024-01-05", daysUntilStockout: 2 },
  { id: "4", product: "Phone Case", sku: "ELEC-012", currentStock: 12, reorderPoint: 25, suggestedOrder: 50, location: "Store Front", supplier: "MobileGear Ltd.", lastRestocked: "2024-01-08", daysUntilStockout: 5 },
  { id: "5", product: "Winter Jacket", sku: "CLTH-007", currentStock: 3, reorderPoint: 10, suggestedOrder: 20, location: "Warehouse B", supplier: "FashionWholesale", lastRestocked: "2024-01-02", daysUntilStockout: 1 },
];

export default function LowStockSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredItems = MOCK_LOW_STOCK.filter((item) =>
    item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const criticalItems = MOCK_LOW_STOCK.filter((item) => item.daysUntilStockout <= 3);
  const warningItems = MOCK_LOW_STOCK.filter((item) => item.daysUntilStockout > 3 && item.daysUntilStockout <= 7);

  const toggleSelectItem = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map((item) => item.id));
    }
  };

  const getUrgencyBadge = (days: number) => {
    if (days <= 3) {
      return { color: "text-red-500 bg-red-500/20", label: t("posInventory.critical") };
    }
    if (days <= 7) {
      return { color: "text-amber-500 bg-amber-500/20", label: t("posInventory.warning") };
    }
    return { color: "text-blue-500 bg-blue-500/20", label: t("posInventory.monitor") };
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.lowStockAlertsTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.lowStockAlertsSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-mail-send-line"></i>
            {t("posInventory.notifySuppliers")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-shopping-cart-line"></i>
            {t("posInventory.createPurchaseOrder")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalAlerts")}</span>
            <i className="ri-alarm-warning-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{MOCK_LOW_STOCK.length}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.criticalItems")}</span>
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-red-500`}>{criticalItems.length}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.warningItems")}</span>
            <i className="ri-alert-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{warningItems.length}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.suggestedOrderValue")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>$12,450</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-filter-line"></i>
              {t("posInventory.filters")}
            </button>
            {selectedItems.length > 0 && (
              <button className="px-4 py-2 rounded-xl bg-blue-500 text-white font-medium flex items-center gap-2">
                <i className="ri-file-list-line"></i>
                {t("posInventory.orderSelected")} ({selectedItems.length})
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                    onChange={selectAll}
                    className="rounded border-gray-400"
                  />
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.product")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.currentStock")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.reorderPoint")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.suggestedOrder")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.supplier")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.urgency")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const urgency = getUrgencyBadge(item.daysUntilStockout);
                return (
                  <tr key={item.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleSelectItem(item.id)}
                        className="rounded border-gray-400"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.product}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.sku}</p>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold text-red-500`}>{item.currentStock}</td>
                    <td className={`px-4 py-3 text-right ${themeClasses.textSecondary}`}>{item.reorderPoint}</td>
                    <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>{item.suggestedOrder}</td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{item.supplier}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgency.color}`}>
                        {item.daysUntilStockout}d - {urgency.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`} title={t("posInventory.orderNow")}>
                          <i className="ri-shopping-cart-line"></i>
                        </button>
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`} title={t("posInventory.viewHistory")}>
                          <i className="ri-history-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
